"use client"

import {useEffect, useMemo} from "react";
import type {CallNoteMetadata, CatalogueFeature, InsightEvidence} from "@devplaninc/codeup";
import {useShallow} from "zustand/react/shallow";
import logger from "@/app/logger";
import type {EvidenceResolvers} from "@/app/components/evidence/evidenceDisplay";
import {
  collectEvidenceHydrationRefs,
  callNoteProviderKey,
  documentEvidenceKey,
  evidenceHydrationKeys,
  ticketKey,
} from "@/app/components/evidence/evidenceHydrationRefs";
import {useRepoProviderMap, useSlackConnected} from "@/hooks/queries/use-integrations";
import {
  emptyEvidenceHydrationData,
} from "@/hooks/queries/stores/evidenceHydrationStore";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";

const EMPTY_EVIDENCE_HYDRATION_DATA = emptyEvidenceHydrationData();

export function useEvidenceHydration(companyId: number, evidence: InsightEvidence[]) {
  const hydrateEvidence = useBoundStore(s => s.hydrateEvidence);
  const data = useBoundStore(useShallow(s => s.evidenceHydration[companyId] ?? EMPTY_EVIDENCE_HYDRATION_DATA));
  const {repoProviderMap} = useRepoProviderMap(companyId);
  const {connected: slackConnected} = useSlackConnected();
  const rootRefs = useMemo(() => collectEvidenceHydrationRefs(evidence), [evidence]);
  const nestedInsightEvidence = useMemo(
    () => rootRefs.refs.flatMap(ref => {
      const target = ref.target;
      if (target?.$case !== "insightId") return [];
      return data.insights.find(insight => insight.id === target.value)?.evidence ?? [];
    }),
    [data.insights, rootRefs],
  );
  const hydrationEvidence = useMemo(
    () => nestedInsightEvidence.length > 0 ? [...evidence, ...nestedInsightEvidence] : evidence,
    [evidence, nestedInsightEvidence],
  );
  const refs = useMemo(() => collectEvidenceHydrationRefs(hydrationEvidence), [hydrationEvidence]);
  const neededKeys = useMemo(() => evidenceHydrationKeys(refs), [refs]);

  useEffect(() => {
    if (neededKeys.length === 0) return;
    void hydrateEvidence(companyId, refs).catch(error => {
      logger.error({error}, "Failed to hydrate evidence");
    });
  }, [companyId, hydrateEvidence, neededKeys.length, refs]);

  const featureMap = useMemo<Map<string, CatalogueFeature>>(
    () => new Map(data.features.map(feature => [feature.id, feature])),
    [data.features],
  );

  const loading = neededKeys.some(key => data.loadingKeys[key]);
  const resolvers = useMemo<EvidenceResolvers>(() => ({
    signals: byId(data.signals, item => item.id),
    changes: byId(data.changes, item => item.changeId),
    personsById: byId(data.persons, item => item.personId),
    featureMap,
    repoProviderMap,
    companyId,
    discussionTopics: byId(data.discussionTopics, item => item.id),
    callNotesByInternalId: byId(data.callNotes, item => item.id),
    callNotesByProviderKey: callNotesByProviderKey(data.callNotes),
    tickets: byId(data.tickets, ticketKey),
    documents: byId(data.documents, document => document.ref ? documentEvidenceKey(document.ref) : ""),
    insights: byId(data.insights, insight => insight.id),
    slackConnected,
  }), [companyId, data.callNotes, data.changes, data.discussionTopics, data.documents, data.insights, data.persons, data.signals, data.tickets, featureMap, repoProviderMap, slackConnected]);

  return {refs, resolvers, loading};
}

function byId<T>(values: T[], idOf: (item: T) => string): Record<string, T> {
  const out: Record<string, T> = {};
  for (const value of values) {
    const id = idOf(value);
    if (id) out[id] = value;
  }
  return out;
}

function callNotesByProviderKey(values: CallNoteMetadata[]): Record<string, CallNoteMetadata> {
  const out: Record<string, CallNoteMetadata> = {};
  for (const value of values) {
    const providerKey = callNoteProviderKey(value.providerType, value.providerNoteId);
    if (providerKey) out[providerKey] = value;
  }
  return out;
}
